import React from 'react';
import { Link } from 'react-router-dom';
import AppsLayout from './AppsLayout';
import Reveal from './components/Reveal';
import Badge from './components/Badge';
import Button from './components/Button';
import { numbcrunch as app } from '../../content/numbcrunch/app';
import { trackClick } from '../../firebase/analytics';
import { APPS_ROUTES } from './routes';
import home1080 from '../../assets/images/numbcrunch/home-1080.webp';
import home540 from '../../assets/images/numbcrunch/home-540.webp';
import gameplay1080 from '../../assets/images/numbcrunch/gameplay-1080.webp';
import gameplay540 from '../../assets/images/numbcrunch/gameplay-540.webp';
import tutorial1080 from '../../assets/images/numbcrunch/tutorial-practice-1080.webp';
import tutorial540 from '../../assets/images/numbcrunch/tutorial-practice-540.webp';

const PAGE = 'apps_numbcrunch';
const SCREENSHOTS = [
  {
    id: 'home',
    title: 'Pick your mode',
    summary: 'Start a solo round, switch to Double Player or explore the tutorial.',
    src: home1080,
    srcSet: `${home540} 540w, ${home1080} 1080w`,
    alt: 'NumbCrunch home screen with Single player selected, Double player, Start, Tutorial, About and settings controls on a wooden background.',
  },
  {
    id: 'gameplay',
    title: 'Read the clues',
    summary: 'The guess board keeps each attempt and its clue beside the number keypad.',
    src: gameplay1080,
    srcSet: `${gameplay540} 540w, ${gameplay1080} 1080w`,
    alt: 'NumbCrunch Single Player round showing guesses 123 with SMYLE and 456 with 2W, empty rows and the number keypad.',
  },
  {
    id: 'tutorial',
    title: 'Try a practice round',
    summary: 'Practice with a known secret and learn what the R and W clues mean.',
    src: tutorial1080,
    srcSet: `${tutorial540} 540w, ${tutorial1080} 1080w`,
    alt: 'NumbCrunch interactive tutorial with practice secret 245, a guess of 325 and the clue 1R, 1W.',
  },
];

export default function NumbCrunchPage() {
  return (
    <AppsLayout
      title="NumbCrunch · Number deduction on Android · Keval Langalia"
      description="NumbCrunch is an offline Android number-guessing game. Follow three-digit clues, play solo or challenge a friend on the same device, with no ads or accounts."
      canonicalPath={APPS_ROUTES.numbcrunch}
      breadcrumbs={[{ label: 'Portfolio', to: APPS_ROUTES.apps, id: 'apps' }, { label: app.name }]}
      page={PAGE}
    >
      <Reveal as="section" className="apps-hero apps-product-hero" aria-labelledby="numbcrunch-heading">
        <div>
          <p className="apps-eyebrow">{app.name} · {app.platform}</p>
          <h1 id="numbcrunch-heading" className="apps-title">{app.tagline}</h1>
          <p className="apps-lead">{app.summary}</p>
          <ul className="apps-badges" aria-label="NumbCrunch details">
            {app.badges.map((badge) => <li key={badge.label}><Badge variant={badge.variant}>{badge.label}</Badge></li>)}
            <li><Badge variant="status">{app.release.label}</Badge></li>
          </ul>
          <div className="apps-hero-actions">
            <a href="#numbcrunch-features" className="kds-btn kds-btn--filled" onClick={trackClick(PAGE, 'hero_how_to_play_button')}>How to play</a>
            <Button to={app.privacyRoute} variant="secondary" onClick={trackClick(PAGE, 'hero_privacy_button')}>Read the privacy policy</Button>
            <Button to={app.termsRoute} variant="secondary" onClick={trackClick(PAGE, 'hero_terms_button')}>Terms of Service</Button>
          </div>
        </div>
        <img className="apps-product-icon" src={app.iconImage} width="512" height="512" alt="NumbCrunch app icon: three golden number tiles showing 3, 7 and 2." />
      </Reveal>

      <section className="apps-section" aria-labelledby="numbcrunch-status-heading">
        <h2 id="numbcrunch-status-heading" className="apps-section-title">Availability</h2>
        <p>{app.release.detail}</p>
      </section>

      <section className="apps-section" aria-labelledby="numbcrunch-screenshots-heading">
        <h2 id="numbcrunch-screenshots-heading" className="apps-section-title">See NumbCrunch in action</h2>
        <p className="apps-muted">Actual app screens from the current development build (October 2026). The public release may differ.</p>
        <ul className="apps-screenshot-grid" aria-label="NumbCrunch screenshot gallery">
          {SCREENSHOTS.map((shot, index) => (
            <Reveal as="li" key={shot.id} delay={index * 70}>
              <figure className="apps-screenshot apps-screenshot--numbcrunch">
                <img
                  src={shot.src}
                  srcSet={shot.srcSet}
                  sizes="(max-width: 37.5rem) 88vw, (max-width: 70rem) 42vw, 20rem"
                  width="1080"
                  height="2400"
                  alt={shot.alt}
                  loading="lazy"
                />
                <figcaption><strong>{shot.title}</strong><span>{shot.summary}</span></figcaption>
              </figure>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="apps-section" id="numbcrunch-features" aria-labelledby="numbcrunch-features-heading">
        <h2 id="numbcrunch-features-heading" className="apps-section-title">What NumbCrunch does</h2>
        <p className="apps-muted">Three different digits, a few useful clues and as many guesses as you need.</p>
        <ul className="apps-features">
          {app.features.map((feature, index) => (
            <Reveal as="li" key={feature.id} className="apps-feature" delay={index * 70}>
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="apps-section" aria-labelledby="numbcrunch-privacy-heading">
        <h2 id="numbcrunch-privacy-heading" className="apps-section-title">Privacy at a glance</h2>
        <ul>{app.privacyHighlights.map((item) => <li key={item}>{item}</li>)}</ul>
        <p>
          The full <Link to={app.privacyRoute} onClick={trackClick(PAGE, 'privacy_section_link')}>NumbCrunch privacy policy</Link> explains on-device information, Android backups, Google Play and privacy for children.
        </p>
      </section>

      <section className="apps-section" id="support" aria-labelledby="numbcrunch-support-heading">
        <h2 id="numbcrunch-support-heading" className="apps-section-title">Support and common questions</h2>
        <p>
          Contact {app.operator} at <a href={`mailto:${app.contactEmail}`} onClick={trackClick(PAGE, 'support_email_link')}>{app.contactEmail}</a> for app support or privacy questions. Include your app version, Android version and a short description of the issue when relevant.
        </p>
        <ul className="apps-faq">
          {app.faq.map((entry) => <li key={entry.question}><h3>{entry.question}</h3><p>{entry.answer}</p></li>)}
        </ul>
      </section>

      <section className="apps-section" aria-labelledby="numbcrunch-about-heading">
        <h2 id="numbcrunch-about-heading" className="apps-section-title">About</h2>
        <p>NumbCrunch is an Android game by {app.operator}. Its Android package is <code>{app.packageName}</code>.</p>
        <div className="apps-hero-actions">
          <Button to={APPS_ROUTES.apps} variant="text" onClick={trackClick(PAGE, 'all_apps_link')}>All projects</Button>
        </div>
      </section>
    </AppsLayout>
  );
}
